import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

/**
 * Service that handles benchmark integration
 */
@Injectable({
  providedIn: 'root'
})
export class BenchmarkService implements OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Subject that emits retriving data
   */
  public retrieving$: Subject<boolean> = new Subject<boolean>()
  /**
   * Subject that deals with filtering
   */
  public filter$: Subject<number> = new Subject()
  /** On destroy implementation */
  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Headers object to send with the request
   */
  Headers = {
    'Content-Type': 'application/json'
  }
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   * @param auth AuthService
   */
  constructor(
    private http: HttpClient,
    public auth: AuthService
  ) {}

  /**
   * Sets a key value pair in the local storage with an expiry time
   * @param key string
   * @param value string
   * @param ttl number
   */
  setWithExpiry(key, value, ttl) {
    const now = new Date()
    const item = {
      value: value,
      expiry: now.getTime() + ttl
    }
    localStorage.setItem(key, JSON.stringify(item))
  }

  /**
   * Gets a value by it's key from local storage, if the expiry date has passed or the key isn't found returns null instead
   * @param key string
   * @returns string | null
   */

  getWithExpiry(key) {
    const itemStr = localStorage.getItem(key)

    if (!itemStr) {
      return null
    }
    const item = JSON.parse(itemStr)
    const now = new Date()

    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key)
      return null
    }
    return item.value
  }

  /**
   * Sends a request to authenticate a user and retrieve a token from benchmark
   * @param email benchmark login email
   * @param password benchmark login password
   * @returns Observable
   */
  connectBenchmark(email, password) {
    return this.http
      .post(
        `${environment.benchmarkExportApi}auth`,
        { Username: email, Password: password },
        {
          responseType: 'json'
        }
      )
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Exports an event to benchmark from the backend
   * @param eventSlug string
   * @param bmToken string
   * @returns Observable
   */
  exportBenchmark(eventSlug, bmToken) {
    return this.http
      .post(
        `${environment.benchmarkExportApi}${this.auth.currentWorkspace.slug}/${eventSlug}`,
        { bmToken: bmToken, userToken: this.auth.getToken(), worsName: this.auth.currentWorkspace.name },
        {
          headers: new HttpHeaders({
            userToken: this.auth.getToken()
          })
        }
      )
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Exports entire workspace events to benchmark from the backend
   * @param bmToken string
   * @returns Observable
   */
  exportWorsBenchmark(bmToken) {
    return this.http
      .post(
        `${environment.benchmarkExportApi}${this.auth.currentWorkspace.slug}`,
        { bmToken: bmToken, userToken: this.auth.getToken(), worsName: this.auth.currentWorkspace.name },
        {
          headers: new HttpHeaders({
            userToken: this.auth.getToken()
          })
        }
      )
      .pipe(takeUntil(this._destroy$))
  }
}
